import React from 'react';
import { Avatar, Tooltip, useTheme, alpha } from '@mui/material';
import { useAuth } from '../../context/AuthContext';

/**
 * UserAvatar Component
 * Displays the current user's avatar with their initials
 * Falls back to email initials if no name is available
 */
const UserAvatar = ({ size = 28, ...props }) => {
  const { userProfile, user } = useAuth();
  const theme = useTheme();

  // Function to get user initials
  const getUserInitials = () => {
    // Priority: display_name > associated_person > email
    const name = userProfile?.display_name || userProfile?.associated_person;

    if (name) {
      // Get first letter of first and last name
      const nameParts = name.trim().split(/\s+/);
      if (nameParts.length >= 2) {
        return `${nameParts[0][0]}${nameParts[nameParts.length - 1][0]}`.toUpperCase();
      }
      // If only one name, use first two letters
      return name.substring(0, 2).toUpperCase();
    }

    // Fallback to email
    const email = userProfile?.email || user?.email || '';
    if (email) {
      return email.substring(0, 2).toUpperCase();
    }

    return 'U';
  };

  // Function to get display name for tooltip
  const getDisplayName = () => {
    return userProfile?.display_name ||
           userProfile?.associated_person ||
           userProfile?.email ||
           user?.email ||
           'Usuario';
  };

  // Generate discrete color from theme with proper contrast
  const getAvatarColor = () => {
    const userId = user?.id || '';
    if (!userId) {
      // Use theme colors for fallback - discrete and harmonious
      return theme.palette.mode === 'dark'
        ? alpha(theme.palette.common.white, 0.15)  // More visible in dark mode
        : alpha(theme.palette.common.black, 0.12); // More visible in light mode
    }

    // Simple hash function to generate color from user ID
    let hash = 0;
    for (let i = 0; i < userId.length; i++) {
      hash = userId.charCodeAt(i) + ((hash << 5) - hash);
    }

    // Convert to HSL color - discrete but with high contrast text
    const hue = Math.abs(hash % 360);
    const saturation = 35; // Higher saturation for better contrast
    const lightness = theme.palette.mode === 'dark' ? 20 : 80; // More extreme values for contrast

    return `hsl(${hue}, ${saturation}%, ${lightness}%)`;
  };

  // Get text color with high contrast
  const getTextColor = () => {
    return theme.palette.mode === 'dark'
      ? alpha(theme.palette.common.white, 0.95)  // Almost white in dark mode
      : alpha(theme.palette.common.black, 0.8);  // Almost black in light mode
  };

  return (
    <Tooltip title={getDisplayName()} arrow>
      <Avatar
        sx={{
          width: size,
          height: size,
          bgcolor: getAvatarColor(),
          color: getTextColor(),
          fontSize: size * 0.45,
          fontWeight: 500,
          cursor: 'pointer',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          ...props.sx
        }}
        {...props}
      >
        {getUserInitials()}
      </Avatar>
    </Tooltip>
  );
};

export default UserAvatar;
